import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Check, Sparkles, Search, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import { Input } from "@/components/ui/input";

export default function Store() {
  const { add, setOpen, items } = useCart();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [categories, setCategories] = useState(["All"]);
  const [query, setQuery] = useState("");
  const [justAdded, setJustAdded] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/store/products"),
      api.get("/store/categories"),
    ]).then(([p, c]) => {
      setProducts(p.data);
      setCategories(c.data.categories);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (category !== "All" && p.category !== category) return false;
      if (q) {
        const hay = `${p.title} ${p.description} ${p.category} ${p.type}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [products, category, query]);

  const inCart = (slug) => items.some((i) => i.slug === slug);

  const handleAdd = (p) => {
    add(p);
    setJustAdded(p.slug);
    setTimeout(() => setJustAdded((s) => (s === p.slug ? null : s)), 1400);
    toast.success(`Added — ${p.title}`);
  };

  const buyNow = (p) => {
    add(p);
    setTimeout(() => setOpen(true), 200);
  };

  return (
    <div data-testid="store-page">
      <PageHero
        overline="Store · Books, Music, Resources"
        title={<>Kingdom teachings <span className="font-accent text-gold-gradient italic">in your hands.</span></>}
        subtitle="Books that renew your mind, worship that shifts your atmosphere, and resources that build your leadership. Every purchase supports the ministry's global reach."
      />

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-28">
        {/* Filters */}
        <Reveal className="mb-10 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Sparkles size={16} className="text-gold-500" />
              <Overline>Catalogue</Overline>
              <span className="text-xs text-white/40" data-testid="store-count">
                {filtered.length} {filtered.length === 1 ? "item" : "items"}
              </span>
            </div>
            <div className="relative w-full md:max-w-xs">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search titles…"
                data-testid="store-search"
                className="pl-11 bg-transparent border-white/15 text-white placeholder:text-white/40 focus:border-gold-400"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="store-categories">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`store-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                  category === c ? "border-gold-400 text-gold-300 bg-gold-500/10" : "border-white/15 text-white/60 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        {filtered.length === 0 ? (
          <p className="text-white/50 py-14 text-center border border-gold-500/15" data-testid="store-empty">
            No products match your search.
          </p>
        ) : (
          <motion.div layout className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="store-grid">
            <AnimatePresence>
              {filtered.map((p, i) => (
                <Reveal key={p.slug} delay={(i % 4) * 0.05}>
                  <motion.article
                    layout
                    className="group flex h-full flex-col border border-gold-500/20 bg-ink-paper/40 overflow-hidden transition-colors duration-300 hover:border-gold-500/50"
                    data-testid={`product-${p.slug}`}
                  >
                    <div className="aspect-[4/5] overflow-hidden bg-ink relative">
                      {p.image && (
                        <img
                          src={p.image}
                          alt={p.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      )}
                      <span className="absolute top-3 left-3 bg-ink/80 border border-gold-500/30 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-gold-400">
                        {p.type}
                      </span>
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-gold-400">{p.category}</p>
                      <h3 className="mt-2 font-serif text-lg text-white leading-snug line-clamp-2">{p.title}</h3>
                      <p className="mt-2 text-sm text-white/60 leading-relaxed line-clamp-3 flex-1">{p.description}</p>
                      <div className="mt-4 flex items-baseline justify-between">
                        <p className="font-serif text-2xl text-gold-300" data-testid={`price-${p.slug}`}>${p.price.toFixed(2)}</p>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">USD</span>
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleAdd(p)}
                          data-testid={`add-${p.slug}`}
                          className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-[11px] uppercase tracking-[0.15em] border transition-all duration-300 ${
                            justAdded === p.slug
                              ? "border-gold-500 bg-gold-500/15 text-gold-200"
                              : inCart(p.slug)
                                ? "border-gold-500/60 text-gold-300 hover:bg-gold-500/10"
                                : "border-gold-500/30 text-white/85 hover:border-gold-400 hover:text-gold-300"
                          }`}
                        >
                          {justAdded === p.slug ? <Check size={13} /> : <ShoppingBag size={13} />}
                          {inCart(p.slug) && justAdded !== p.slug ? "In Cart" : "Add"}
                        </button>
                        <button
                          onClick={() => buyNow(p)}
                          data-testid={`buy-${p.slug}`}
                          className="inline-flex items-center justify-center gap-1.5 bg-gold-500 text-ink font-semibold px-3 py-2.5 text-[11px] uppercase tracking-[0.15em] hover:bg-gold-400 transition-colors"
                        >
                          Buy Now
                        </button>
                      </div>
                      {p.has_sample && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/store/sample/${p.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`sample-${p.slug}`}
                          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 border border-white/15 py-2 text-[11px] uppercase tracking-[0.15em] text-white/70 hover:text-gold-300 hover:border-gold-500/40 transition-colors"
                        >
                          <BookOpen size={12} /> Read a Sample
                        </a>
                      )}
                    </div>
                  </motion.article>
                </Reveal>
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Trust bar */}
        <Reveal className="mt-20 border border-gold-500/20 bg-ink-paper/30 p-8 md:p-10 grid gap-6 sm:grid-cols-3 text-center">
          <div>
            <p className="font-serif text-lg text-white">Secure Checkout</p>
            <p className="mt-1 text-sm text-white/55">256-bit encryption via Stripe</p>
          </div>
          <div>
            <p className="font-serif text-lg text-white">Ministry Impact</p>
            <p className="mt-1 text-sm text-white/55">Every purchase fuels global outreach</p>
          </div>
          <div>
            <p className="font-serif text-lg text-white">Instant Access</p>
            <p className="mt-1 text-sm text-white/55">Digital items delivered by email</p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
